import React, { useState, useEffect, useRef, useLayoutEffect, useContext, useMemo, useCallback } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { MdOutlineCampaign } from "react-icons/md";
import { Carousel } from "react-responsive-carousel";
import ImageComponent from "../../common/ImageComponent";
import { _imagesURL, adminBaseURL } from "../../../constants/API";
import "../../agenda/AgendaUpcomingSidebar.css";
import "../../agenda/AgendaFoundation.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Language, Sponsorpost } from "../../../App";
import { LOGIN, NEW_CONTEST } from "../../../constants/RoutesUrl";
import { getStoredAdRatio, syncAdMediaSize } from "../../../utils/splashAdsUtils";

export default function FeedAdvertisement({ right, left, ads, loading, type }) {
  const navigate = useNavigate();
  const { updateContext } = useContext(Sponsorpost);
  const [currentSlide, setCurrentSlide] = useState(0);
  const carouselRef = useRef(null);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const { lang } = useContext(Language);

  // Measure width before first paint so ratio is correct immediately
  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const update = () => setContainerWidth(containerRef.current?.offsetWidth || 0);
    const ro = new ResizeObserver(update);
    update();
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Every API placement can wrap the usable advertisement in `general_ad`.
  // Normalize it before rendering so course ads do not try to load an
  // undefined wrapper media field and remain on the image loader.
  const transformedAds = useMemo(() => {
    if (!Array.isArray(ads)) return [];

    return ads
      .map((ad) => {
        const actualAd = ad?.general_ad || ad;
        return {
          ...actualAd,
          id: actualAd?.id ?? ad?.id,
          media: actualAd?.media || ad?.media,
        };
      })
      .filter((ad) => Boolean(ad?.media));
  }, [ads]);

  const totalSlides = useMemo(() => transformedAds?.length || 0, [transformedAds?.length]);

  const { data } = JSON.parse(localStorage.getItem("SignupData") || "{}");

  function handleRedirect(ad) {
    switch (ad.internal_model) {
      case "course": navigate(`/course-detail/${ad.course_id}`); break;
      case "profile": navigate(`/profile/${ad.user_id}`); break;
      case "clinical_session": navigate(`/session-details/${ad.post_id}`); break;
      case "webinar": navigate(`/webinar-details/${ad.post_id}`); break;
      case "clinical_study": navigate(`/post/${ad.post_id}`); break;
      case "event": navigate(`/event-details/${ad.event_id}`); break;
      default: console.warn("Unknown internal_model type:", ad.internal_model);
    }
  }

  const handleAdClick = async (ad) => {
    if (!ad) return;
    if (ad.type === "external") {
      if (ad.link) window.open(ad.link, "_blank");
      return;
    } else if (ad.type === "internal") {
      handleRedirect(ad);
    } else {
      navigate(NEW_CONTEST, { state: { dynamicAd: ad } });
    }
    updateContext(ad);
    if (data?.token) {
      try {
        await axios.post(
          `${adminBaseURL}user/ads/view`,
          { device: "Web", ad_id: ad.id, type: "view" },
          { headers: { Authorization: `Bearer ${data.token}` } }
        );
      } catch (error) {
        console.error("Error in API call:", error);
      }
    } else {
      navigate(LOGIN);
    }
  };

  // ── POSITIONING UNTOUCHED ──
  const containerStyles = useMemo(
    () => ({
      width: right ? "335px" : left ? "275px" : "100%",
      maxWidth: right ? "335px" : left ? "275px" : "100%",
      borderRadius: right || left ? "24px" : "16px",
      border: right || left ? "1px solid #D7E0EA" : "1px solid #EBF1F6",
      background: "#FFFFFF",
      boxShadow: right || left
        ? "0px 8px 30px rgba(0, 186, 218, 0.06), 0px 2px 8px rgba(0, 0, 0, 0.03)"
        : "0 2px 8px rgba(11, 19, 43, 0.03)",
      position: "static",
      top: "auto",
      overflow: "hidden",
      transition: "box-shadow 0.25s ease, border-color 0.25s ease",
      fontFamily: "'Onest', sans-serif",
    }),
    [left, right]
  );

  const placementKey = right ? "home_right" : "home_left";

  useEffect(() => {
    if (transformedAds.length > 0 && transformedAds[0]?.media_size) {
      const parsed = parseFloat(transformedAds[0].media_size);
      if (Number.isFinite(parsed) && parsed > 0) {
        syncAdMediaSize(placementKey, parsed);
      }
    }
  }, [transformedAds, placementKey]);

  // Dynamic height from first ad's media_size — fallbacks to cached localStorage ratio
  const sharedHeight = useMemo(() => {
    const firstAd = transformedAds[0];
    const parsedSize = Number(firstAd?.media_size);
    const mediaSize = parsedSize > 0 ? parsedSize : getStoredAdRatio(placementKey, 1.5);
    const safeWidth = containerWidth > 0 ? containerWidth : (right ? 335 : 275);

    return Math.round(safeWidth / mediaSize);
  }, [transformedAds, containerWidth, right, placementKey]);

  if (loading) {
    return (
      <div className="d-xl-block d-none mb-3" style={containerStyles}>
        {/* Glass header shimmer */}
        <div
          className="d-flex justify-content-between align-items-center px-3"
          style={{ height: "46px", borderBottom: "1px solid #F1F5F9", background: "rgba(255,255,255,0.9)" }}
        >
          <div className="d-flex align-items-center gap-2">
            <div
              className="agenda-shimmer-block"
              style={{ width: 26, height: 26, borderRadius: "50%" }}
            />
            <div
              className="agenda-shimmer-block"
              style={{ width: 90, height: 12, borderRadius: 5 }}
            />
          </div>
          <div
            className="agenda-shimmer-block"
            style={{ width: 66, height: 20, borderRadius: 999 }}
          />
        </div>

        {/* Image area shimmer */}
        <div
          className="agenda-shimmer-block"
          style={{ width: "100%", height: sharedHeight || (right ? 335 : 275), borderRadius: 0 }}
        />

        {/* Dot strip shimmer */}
        <div
          className="d-flex justify-content-center align-items-center gap-2"
          style={{ height: "37px", borderTop: "1px solid #F1F5F9", background: "#FAFCFE" }}
        >
          <div className="agenda-shimmer-block" style={{ width: 6, height: 5, borderRadius: 3 }} />
          <div className="agenda-shimmer-block" style={{ width: 18, height: 5, borderRadius: 3 }} />
          <div className="agenda-shimmer-block" style={{ width: 6, height: 5, borderRadius: 3 }} />
        </div>
      </div>
    );
  }

  if (!transformedAds || transformedAds.length === 0) return null;

  return (
    <>
      <style>{`
        /* ── Glass header bar over image ── */
        .ad-glass-header {
          position: absolute;
          top: 0; left: 0; right: 0;
          z-index: 10;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 14px;
          background: rgba(255,255,255,0.78);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border-bottom: 1px solid rgba(255,255,255,0.5);
        }

        /* ── Gradient overlay bottom ── */
        .ad-img-overlay {
          position: absolute;
          bottom: 0; left: 0; right: 0;
          height: 50%;
          background: linear-gradient(to top, rgba(10,20,35,.80) 0%, rgba(10,20,35,.35) 50%, transparent 100%);
          pointer-events: none;
          z-index: 5;
        }

        /* ── CTA over image ── */
        .ad-cta-area {
          position: absolute;
          bottom: 14px;
          left: 14px;
          right: 14px;
          z-index: 6;
          display: flex;
          align-items: center;
          justify-content: flex-end;
        }
        .ad-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 7px 15px;
          border-radius: 999px;
          border: none;
          background: linear-gradient(135deg, #00BADA 0%, #0099B8 100%);
          color: #FFFFFF;
          font-size: 12px;
          font-weight: 600;
          font-family: 'Onest', sans-serif;
          cursor: pointer;
          box-shadow: 0 4px 14px rgba(0,186,218,.42);
          transition: all .2s ease;
          white-space: nowrap;
        }
        .ad-cta-btn:hover {
          background: linear-gradient(135deg, #00CFE8 0%, #00BADA 100%);
          box-shadow: 0 6px 20px rgba(0,186,218,.55);
          transform: translateY(-1px);
        }

        /* ── Bottom bar controls ── */
        .ad-bottom-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 18px;
          border-top: 1px solid #F1F5F9;
          background: #FFFFFF;
        }
        .ad-bottom-nav-btn {
          width: 26px;
          height: 26px;
          border-radius: 50%;
          border: 1px solid #E2E8F0;
          background: #F8FAFC;
          color: #475569;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          padding: 0;
          line-height: 0;
          transition: all .18s ease;
          outline: none;
          flex-shrink: 0;
        }
        .ad-bottom-nav-btn svg {
          display: block;
          flex-shrink: 0;
        }
        .ad-bottom-nav-btn:hover {
          background: #00BADA;
          border-color: #00BADA;
          color: #FFFFFF;
          box-shadow: 0 2px 8px rgba(0,186,218,.35);
          transform: scale(1.08);
        }

        /* ── Dot strip ── */
        .ad-dot {
          height: 5px;
          border-radius: 3px;
          transition: all .28s cubic-bezier(.4,0,.2,1);
          cursor: pointer;
        }

        /* ── Carousel resets ── */
        .ad-img-wrap .carousel-root,
        .ad-img-wrap .carousel,
        .ad-img-wrap .carousel.carousel-slider,
        .ad-img-wrap .slider-wrapper { width: 100%; }
        .ad-img-wrap .carousel .slide { background: #F0F4F8; }
      `}</style>

      <div ref={containerRef} className="d-xl-block d-none mb-3" style={containerStyles}>

        {/* ── Dedicated header row (outside image) ── */}
        <div
          className="d-flex justify-content-between align-items-center px-3"
          style={{ height: "46px", borderBottom: "1px solid #F1F5F9", background: "#fff" }}
        >
          <div className="d-flex align-items-center gap-2">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
              style={{ width: 26, height: 26, background: "#E8FBFF", color: "#00BADA", border: "1px solid #BCEEFA" }}
            >
              <MdOutlineCampaign size={14} />
            </div>
            <span className="fs-15 roboto-medium mb-0" style={{ color: "#0F1A2B", lineHeight: 1.2, fontWeight: 600 }}>
              {lang?.advertisement || "Advertisement"}
            </span>
          </div>
          <span
            className="badge rounded-pill"
            style={{ fontSize: 9, fontWeight: 600, background: "#E8FBFF", color: "#00BADA", border: "1px solid #BCEEFA", padding: "3px 8px", letterSpacing: ".3px" }}
          >
            SPONSORED
          </span>
        </div>

        {/* ── Clean image area — no overlays ── */}
        <div className="ad-img-wrap" style={{ position: "relative", height: `${sharedHeight}px`, overflow: "hidden" }}>

          {/* Carousel */}
          <Carousel
            ref={carouselRef}
            showIndicators={false}
            showThumbs={false}
            showStatus={false}
            onChange={setCurrentSlide}
            showArrows={false}
            autoPlay={true}
            infiniteLoop={true}
            interval={5000}
          >
            {transformedAds.map((ad) => (
              <div
                style={{ height: `${sharedHeight}px`, cursor: "pointer" }}
                key={ad.id}
                onClick={() => handleAdClick(ad)}
                aria-label={`Advertisement ${ad.id}`}
              >
                <ImageComponent
                  src={`${_imagesURL}${ad.media}`}
                  compClassName="object-cover"
                  img_height="100%"
                  img_width="100%"
                  loader_height="100%"
                  loader_width="100%"
                  onClick={() => handleAdClick(ad)}
                />
              </div>
            ))}
          </Carousel>
        </div>

        {/* ── Bottom bar controls (Prev, Dots, Next) ── */}
        {totalSlides > 1 && (
          <div className="ad-bottom-bar">
            {/* Previous Button */}
            <button
              type="button"
              className="ad-bottom-nav-btn"
              onClick={(e) => {
                e.stopPropagation();
                carouselRef.current?.moveTo(
                  currentSlide === 0 ? totalSlides - 1 : currentSlide - 1
                );
              }}
              aria-label="Previous advertisement"
            >
              <FiChevronLeft size={15} strokeWidth={2.4} style={{ transform: "translateX(-1px)" }} />
            </button>

            {/* Centered Dots */}
            <div className="d-flex align-items-center gap-2">
              {totalSlides <= 8 ? (
                transformedAds.map((_, idx) => (
                  <div
                    key={idx}
                    className="ad-dot"
                    style={{
                      width: idx === currentSlide ? "20px" : "6px",
                      background: idx === currentSlide ? "#00BADA" : "#D0DDE8",
                    }}
                    onClick={() => carouselRef.current?.moveTo(idx)}
                  />
                ))
              ) : (
                <span style={{ fontSize: 12, fontWeight: 700, color: "#00BADA" }}>
                  {currentSlide + 1} / {totalSlides}
                </span>
              )}
            </div>

            {/* Next Button */}
            <button
              type="button"
              className="ad-bottom-nav-btn"
              onClick={(e) => {
                e.stopPropagation();
                carouselRef.current?.moveTo(
                  currentSlide === totalSlides - 1 ? 0 : currentSlide + 1
                );
              }}
              aria-label="Next advertisement"
            >
              <FiChevronRight size={15} strokeWidth={2.4} style={{ transform: "translateX(1px)" }} />
            </button>
          </div>
        )}
      </div>
    </>
  );
}
